import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tappable } from '@vectord/components';
import { TypeUtils } from '@vectord/utils';
import classNames from 'classnames';
import cogoToast from 'cogo-toast';
import compact from 'lodash/compact';

import { useUpdateSbatGroupInstructorsMutation } from '@classCreation/services/sbatGroupInstructorService';
import { VIEW_SCHEDULE_PAGE_TRACKINGS } from '@classCreation/data/viewSchedulePageTrackings';
import analytics from 'utils/analytics';
import FieldErrors from 'components/FieldErrors';
import Form from 'components/Form';
import generatePreviousSbatGroupsInstructorIds from '@classCreation/utils/generatePreviousSbatGroupsInstructorIds';
import generateSbatGroupInstructors from '@classCreation/utils/generateSbatGroupInstructors';
import SbatGroupOtherInstructorSelect from '../SbatGroupOtherInstructorSelect';
import SbatGroupPrimaryInstructorSelect from '../SbatGroupPrimaryInstructorSelect';
import SbatGroupSecondaryInstructorSelect from '../SbatGroupSecondaryInstructorSelect';
import styles from './EditSbatGroupInstructorsForm.module.scss';
import useSbatGroupConfirmation from '@classCreation/hooks/useSbatGroupConfirmation/useSbatGroupConfirmation';

export default function EditSbatGroupInstructorsForm({
  sbatGroup,
  isEditing,
  setIsEditing,
}) {
  const [instructors, setInstructors] = useState(() =>
    generateSbatGroupInstructors(sbatGroup.sbat_group_instructors)
  );
  const [
    updateInstructors,
    { isSuccess, error, isLoading: isUpdating, reset },
  ] = useUpdateSbatGroupInstructorsMutation();

  const handleInstructorsUpdate = useCallback(() => {
    updateInstructors({
      sbatGroupId: sbatGroup.id,
      instructors,
    });
  }, [instructors, sbatGroup.id, updateInstructors]);

  const handleSbatGroupInstructorsUpdate = useCallback(() => {
    const newOtherInstructorIds = instructors?.other?.map(
      (instructor) => instructor?.value
    );
    const {
      previousPrimaryInstructorIds,
      previousSecondaryInstructorIds,
      previousOtherInstructorIds,
    } = generatePreviousSbatGroupsInstructorIds({ [sbatGroup.id]: sbatGroup });

    analytics.click({
      click_type: VIEW_SCHEDULE_PAGE_TRACKINGS.updateInstructors,
      custom: {
        class_id: sbatGroup.id,
        previous_primary_instructor_id: previousPrimaryInstructorIds,
        previous_secondary_instructor_id: previousSecondaryInstructorIds,
        previous_other_instructor_ids: previousOtherInstructorIds,
        new_primary_instructor_id: instructors?.primary?.value,
        new_secondary_instructor_id: instructors?.secondary?.value,
        new_other_instructor_ids: newOtherInstructorIds,
      },
    });

    handleInstructorsUpdate();
  }, [sbatGroup, handleInstructorsUpdate, instructors]);

  const handleSubmit = useSbatGroupConfirmation(
    handleSbatGroupInstructorsUpdate,
    sbatGroup
  );

  const excludedInstructorIds = useMemo(() => {
    return compact(
      [instructors?.primary, instructors?.secondary, instructors?.other].flat()
    ).map((i) => i.value);
  }, [instructors?.other, instructors?.primary, instructors?.secondary]);

  useEffect(() => {
    if (!isEditing) {
      setInstructors(
        generateSbatGroupInstructors(sbatGroup.sbat_group_instructors)
      );
      if (error) {
        reset();
      }
    }
  }, [error, isEditing, reset, sbatGroup.sbat_group_instructors]);

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success('Instructors are updated successfully!');
      setIsEditing(false);
    }
    if (error) {
      if (TypeUtils.isString(error.data?.message)) {
        cogoToast.error(error.data.message);
      } else {
        cogoToast.error(
          'There was some problem in updating the Instructors. Please try again later!'
        );
      }
    }
  }, [error, isSuccess, setIsEditing]);

  return (
    <Form onSubmit={handleSubmit}>
      <div className="row wrap">
        <SbatGroupPrimaryInstructorSelect
          excludedInstructorIds={excludedInstructorIds}
          className={classNames('m-b-xs', styles.select)}
          isDisabled={!isEditing}
          value={instructors?.primary || null}
          onInstructorsChange={setInstructors}
        />
        <SbatGroupSecondaryInstructorSelect
          excludedInstructorIds={excludedInstructorIds}
          className={classNames('m-b-xs', styles.select)}
          isDisabled={!isEditing}
          value={instructors?.secondary || null}
          onInstructorsChange={setInstructors}
          isClearable
        />
      </div>
      <SbatGroupOtherInstructorSelect
        excludedInstructorIds={excludedInstructorIds}
        isDisabled={!isEditing}
        className={styles.select}
        value={instructors?.other || []}
        onInstructorsChange={setInstructors}
      />
      <FieldErrors
        name="base"
        errors={TypeUtils.isObject(error?.data?.message) && error.data.message}
      />
      <FieldErrors
        name="instructor_id"
        errors={TypeUtils.isObject(error?.data?.message) && error.data.message}
      />
      {isEditing && (
        <Tappable
          component="button"
          type="submit"
          disabled={isUpdating}
          className="btn btn-primary btn-long m-t-xs"
        >
          Update Instructors
        </Tappable>
      )}
    </Form>
  );
}
